import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  Handshake, Sparkles, TrendingUp, Clapperboard, X, CheckCircle2, ArrowUpRight,
} from "lucide-react";
import { useTheme } from "../context/ThemeContext";
import { useAirline } from "../context/AirlineContext";
import { contentSources } from "../data/contentSources";
import RollingRow from "../shared/RollingRow";

const caseStudies = [
  {
    id: "partnerships",
    icon: Handshake,
    title: "Content Partnerships",
    customer: "Malaysia Airlines",
    date: "Q3 2024",
    description: "Exclusive Hollywood premieres and regional blockbusters, secured together.",
    bullets: [
      "Exclusive early-window rights for 15+ major studio releases",
      "Multi-territory licensing across APAC routes",
      "Regional content from 8 local distributors",
      "Revenue-sharing model with content providers",
    ],
    outcome:
      "Broader release windows and lower per-title cost through consolidated deals across studios and regional distributors.",
    image: "https://images.unsplash.com/photo-1536440136628-849c177e76a1?w=1200&h=760&fit=crop",
  },
  {
    id: "curations",
    icon: Sparkles,
    title: "Bespoke Curations",
    customer: "Jazeera Airways",
    date: "Q1 2024",
    description: "A catalogue that speaks in your passengers' language, culture and rhythm.",
    bullets: [
      "Route-specific playlists for 25+ destinations",
      "500+ hours of Arabic-language content",
      "Themed collections for Ramadan and Eid",
      "AI-driven personalisation engine",
    ],
    outcome:
      "Passengers stayed with content longer and rated the entertainment experience materially higher post-launch.",
    image: "https://images.unsplash.com/photo-1485846234645-a62644f84728?w=1200&h=760&fit=crop",
  },
  {
    id: "marketing",
    icon: TrendingUp,
    title: "Marketing Activations",
    customer: "Air India",
    date: "Q4 2024",
    description: "One brand story across IFE and the ground — studios, tourism boards, social.",
    bullets: [
      "Exclusive movie premiere tie-ins with studio partners",
      "Interactive destination guides with tourism boards",
      "Branded entertainment channels for premium cabins",
      "Cross-platform social media campaigns",
    ],
    outcome:
      "Reach at scale across ground and IFE, plus a new ancillary revenue line from branded content partnerships.",
    image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200&h=760&fit=crop",
  },
  {
    id: "cgi",
    icon: Clapperboard,
    title: "CGI & Safety Films",
    customer: "Kenya Airways",
    date: "Q2 2024",
    description: "Safety and destination films rendered in 4K CGI — a heritage told visually.",
    bullets: [
      "4K CGI safety demonstration videos",
      "Animated destination showcases for 30+ cities",
      "Branded welcome sequences for all cabin classes",
      "Modular template system for seasonal updates",
    ],
    outcome:
      "Safety briefings passengers actually watch, and destination visuals that carry the airline's brand end to end.",
    image: "https://images.unsplash.com/photo-1574717024653-61fd2cf4d44d?w=1200&h=760&fit=crop",
  },
];

const ICONS = { Handshake, Sparkles, TrendingUp, Clapperboard };

// Airlines may override the case studies via airline.content.personalCaseStudies.
const useCaseStudies = () => {
  const { airline } = useAirline();
  const custom = airline?.content?.personalCaseStudies;
  return custom ? custom.map((s) => ({ ...s, icon: ICONS[s.iconName] || Sparkles })) : caseStudies;
};

// Which content types get a rolling row + which direction
const galleryRows = [
  { label: "Movies", key: "Movies", direction: "left" },
  { label: "TV", key: "TV", direction: "right" },
  { label: "Audio", key: "Audio", direction: "left" },
  { label: "OTT", key: "OTT", direction: "right" },
];

const CaseStudyOverlay = ({ study, isDark, onClose }) => {
  useEffect(() => {
    if (!study) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [study, onClose]);
  if (!study) return null;
  const Icon = study.icon;

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={onClose}>
      <div className={`absolute inset-0 ${isDark ? "bg-black/70" : "bg-black/40"}`} style={{ backdropFilter: "blur(8px)" }} />
      <div
        onClick={(e) => e.stopPropagation()}
        className={`relative w-full max-w-4xl max-h-[85vh] rounded-3xl overflow-hidden ${
          isDark ? "bg-[#0b0b0d] border border-white/10" : "bg-white border border-black/10"
        }`}
        style={{ animation: "fadeUp 0.35s cubic-bezier(0.22,1,0.36,1)" }}
      >
        <button
          onClick={onClose}
          data-testid="case-study-close"
          className={`absolute top-4 right-4 z-10 w-9 h-9 rounded-full flex items-center justify-center ${
            isDark ? "text-white/70 hover:bg-white/10" : "text-black/60 hover:bg-black/5"
          }`}
        >
          <X className="w-5 h-5" />
        </button>
        <div className="flex flex-col lg:flex-row h-full overflow-y-auto">
          <div className="lg:w-1/2 h-56 lg:h-auto relative flex-shrink-0">
            <img src={study.image} alt={study.title} className="w-full h-full object-cover" />
          </div>
          <div className="lg:w-1/2 p-7 flex flex-col">
            <div className="flex items-center gap-3 mb-5">
              <div className={`w-10 h-10 rounded-2xl flex items-center justify-center ${isDark ? "bg-white/[0.06]" : "bg-black/[0.05]"}`}>
                <Icon className="w-4 h-4" style={{ color: "#A855F7" }} />
              </div>
              <div>
                <h3 className={`text-[22px] font-semibold tracking-tight leading-tight ${isDark ? "text-white" : "text-[#1d1d1f]"}`}>{study.title}</h3>
                <div className={`text-[12px] mt-0.5 ${isDark ? "text-white/50" : "text-black/50"}`}>{study.customer}{study.date ? ` · ${study.date}` : ""}</div>
              </div>
            </div>
            <p className={`text-[15px] leading-relaxed mb-5 ${isDark ? "text-white/75" : "text-black/70"}`}>{study.description}</p>
            <ul className="space-y-2.5 mb-6 flex-1">
              {study.bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="w-3.5 h-3.5 mt-1 flex-shrink-0" style={{ color: "#A855F7" }} />
                  <span className={`text-[13px] leading-snug ${isDark ? "text-white/70" : "text-black/65"}`}>{b}</span>
                </li>
              ))}
            </ul>
            <div className={`rounded-xl px-4 py-3 border-l-[3px]`} style={{ borderColor: "#A855F7", backgroundColor: isDark ? "rgba(168,85,247,0.10)" : "rgba(168,85,247,0.08)" }}>
              <div className="k-eyebrow" style={{ color: "#A855F7" }}>Outcome</div>
              <p className={`mt-1 text-[13px] font-medium ${isDark ? "text-white" : "text-[#1d1d1f]"}`}>{study.outcome}</p>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

const SlidePersonal = () => {
  const { isDark } = useTheme();
  const [active, setActive] = useState(null);
  const studies = useCaseStudies();

  return (
    <div>
      <div className="k-eyebrow" style={{ color: "#A855F7" }}>Personal — Entertainment</div>
      <h2 className={`k-h2 mt-3 max-w-[15ch] ${isDark ? "text-white" : "text-[#1d1d1f]"}`}>
        Made for every
        <br />
        <span style={{ color: "#A855F7" }}>route, every rider.</span>
      </h2>
      <p className={`k-lead mt-5 max-w-[80ch] ${isDark ? "text-white/70" : "text-black/60"}`}>
        Content curated by region, route and demographic — branded to feel
        unmistakably yours. From premium studio partnerships to bespoke
        cultural programming.
      </p>

      <div className="mt-8 grid grid-cols-12 gap-12 items-start">
        {/* Gallery rows — left */}
        <div className="col-span-12 lg:col-span-9 space-y-4">
          {galleryRows.map((row) => (
            <div key={row.key} className="flex items-center gap-5">
              <div className="w-20 flex-shrink-0">
                <div className="k-eyebrow" style={{ color: "#A855F7" }}>{row.label}</div>
              </div>
              <div className="flex-1 min-w-0">
                <RollingRow items={contentSources[row.key] || []} direction={row.direction} speed={45} tileSize={72} />
              </div>
            </div>
          ))}
        </div>

        {/* Selected work — right slim column */}
        <div className="col-span-12 lg:col-span-3">
          <div className="k-eyebrow" style={{ color: isDark ? "rgba(255,255,255,0.45)" : "rgba(0,0,0,0.45)" }}>
            Selected work
          </div>
          <div className="mt-4 flex flex-col gap-2">
            {studies.map((s) => {
              const Icon = s.icon;
              return (
                <button
                  key={s.id}
                  onClick={() => setActive(s)}
                  data-testid={`case-study-${s.id}`}
                  className={`w-full text-left flex flex-col gap-0.5 px-3 py-2.5 rounded-xl transition-colors ${
                    isDark
                      ? "bg-white/[0.05] text-white border border-white/10 hover:bg-white/[0.10]"
                      : "bg-white text-[#1d1d1f] border border-black/10 hover:border-black/25"
                  }`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <Icon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "#A855F7" }} />
                    <span className="text-[12px] font-semibold truncate flex-1">{s.title}</span>
                    <ArrowUpRight className="w-3 h-3 flex-shrink-0" style={{ color: "#A855F7" }} />
                  </div>
                  <div className={`text-[10px] pl-[22px] ${isDark ? "text-white/45" : "text-black/45"}`}>
                    {s.customer}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <CaseStudyOverlay study={active} isDark={isDark} onClose={() => setActive(null)} />
    </div>
  );
};

export default SlidePersonal;
